import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Award, Target, Heart, Zap, Code, Cpu, Cloud, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import ScrollReveal, { StaggerGroup } from '@/components/ScrollReveal';
import StarBorderButton from '@/components/StarBorderButton';
import SpotlightCard from '@/components/SpotlightCard';

export default function About() {
  const skills = [
    {
      icon: <Code size={28} />,
      category: 'Web Development',
      items: ['React', 'Next.js', 'Node.js', 'FastAPI', 'PostgreSQL', 'MongoDB']
    },
    {
      icon: <Cpu size={28} />,
      category: 'IoT & Embedded',
      items: ['ESP32/ESP8266', 'Arduino', 'MQTT', 'Sensor Integration', 'Firmware Development']
    },
    {
      icon: <Cloud size={28} />,
      category: 'DevOps & Cloud',
      items: ['Docker', 'AWS', 'DigitalOcean', 'Nginx', 'CI/CD', 'Linux Server']
    },
    {
      icon: <BookOpen size={28} />,
      category: 'Engineering Tools',
      items: ['MATLAB', 'AutoCAD', 'Fusion 360', 'KiCAD', 'PCB Design']
    }
  ];

  const values = [
    {
      icon: <Target size={32} />,
      title: 'Practical & Result-Oriented',
      description: 'Fokus pada solusi yang bisa diimplementasikan dan memberikan impact nyata.'
    },
    {
      icon: <Heart size={32} />,
      title: 'Passionate About Learning',
      description: 'Selalu update dengan teknologi baru dan senang berbagi knowledge dengan orang lain.'
    },
    {
      icon: <Zap size={32} />,
      title: 'Fast & Reliable',
      description: 'Komunikasi responsif dan deadline-oriented tanpa mengorbankan kualitas.'
    },
    {
      icon: <Award size={32} />,
      title: 'Quality-Focused',
      description: 'Setiap project dikerjakan dengan attention to detail dan best practices.'
    }
  ];

  const journey = [
    {
      year: '2022',
      title: 'Memulai Programming',
      description: 'Belajar HTML, CSS, JavaScript dan membuat website pertama.'
    },
    {
      year: '2023',
      title: 'Eksplorasi IoT',
      description: 'Mulai bereksperimen dengan Arduino dan ESP32, membuat project monitoring sederhana.'
    },
    {
      year: '2024',
      title: 'Full-Stack Development',
      description: 'Mendalami React, Node.js, dan database. Mengerjakan project client pertama.'
    },
    {
      year: '2024',
      title: 'DevOps & Cloud',
      description: 'Belajar deployment, Docker, dan cloud infrastructure untuk production apps.'
    },
    {
      year: '2025-Now',
      title: 'Arnata Project',
      description: 'Membangun Arnata sebagai platform untuk membantu mahasiswa dan UMKM dalam teknologi.'
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="pt-32 pb-20 px-6 noise-overlay">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-medium mb-4">
                About Me
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="about-title">
                Mahasiswa Teknik Elektro
                <br />
                <span className="gradient-text">Engineering Enthusiast</span>
              </h1>
              <p className="text-lg text-slate-300 mb-6">
                Halo! Saya seorang mahasiswa Teknik Elektro yang passionate di bidang web development, IoT, ML, dan engineering tools. 
                Percaya bahwa teknologi harus accessible dan praktis untuk semua orang.
              </p>
              <p className="text-slate-400 mb-8">
                Melalui Arnata, saya ingin membantu mahasiswa dan UMKM mewujudkan project teknologi mereka 
                dari konsep hingga implementasi. Senang belajar hal baru dan berbagi knowledge dengan komunitas.
              </p>
              <Link to="/contact">
                <StarBorderButton className="btn-primary" data-testid="about-cta">
                  Mari Berkolaborasi
                </StarBorderButton>
              </Link>
            </div>

            <div className="relative">
              <Card className="glass-effect border-slate-700 p-8">
                <div className="aspect-square bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-lg flex flex-col items-center justify-center mb-6 p-6">
                  <img 
                    src="/assets/arnata-logo.png" 
                    alt="Arnata Logo" 
                    className="w-full h-full object-contain rounded-xl transition-transform hover:scale-105 duration-300"
                  />
                  <p className="text-slate-300 text-lg font-semibold mt-4">Engineering Excellence</p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-cyan-400 mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>2+</div>
                    <div className="text-slate-400 text-sm">Years Experience</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-cyan-400 mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>30+</div>
                    <div className="text-slate-400 text-sm">Projects Done</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-cyan-400 mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>30+</div>
                    <div className="text-slate-400 text-sm">Happy Clients</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-cyan-400 mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>100%</div>
                    <div className="text-slate-400 text-sm">Commitment</div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Skills */}
      <section className="py-20 px-6 bg-slate-900/50" data-testid="skills-section">
        <div className="container mx-auto">
          <ScrollReveal animation="fade-up" className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Tech Stack & Skills
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Tools dan teknologi yang saya gunakan untuk mengerjakan project
            </p>
          </ScrollReveal>

          <StaggerGroup 
            staggerDelay={0.15}
            animation="fade-up"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {skills.map((skill, index) => (
              <SpotlightCard key={index} className="glass-effect p-6 border-slate-700 card-hover group hover:scale-105 hover:border-cyan-500/50 hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300" data-testid={`skill-card-${index}`}>
                <div className="text-cyan-400 mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">{skill.icon}</div>
                <h3 className="text-lg font-semibold text-white mb-3 transition-colors duration-300 group-hover:text-cyan-400" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {skill.category}
                </h3>
                <div className="space-y-1">
                  {skill.items.map((item, i) => (
                    <div key={i} className="text-sm text-slate-400 transition-colors duration-200 hover:text-white hover:translate-x-1 transform">• {item}</div>
                  ))}
                </div>
              </SpotlightCard>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-6" data-testid="values-section">
        <div className="container mx-auto">
          <ScrollReveal animation="fade-up" className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Core Values
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Prinsip yang saya pegang dalam setiap project dan kolaborasi
            </p>
          </ScrollReveal>

          <StaggerGroup 
            staggerDelay={0.2}
            animation="scale"
            className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto"
          >
            {values.map((value, index) => (
              <SpotlightCard key={index} className="glass-effect p-8 border-slate-700 group hover:scale-105 hover:border-cyan-500/50 hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300" data-testid={`value-card-${index}`}>
                <div className="text-cyan-400 mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">{value.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-2 transition-colors duration-300 group-hover:text-cyan-400" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {value.title}
                </h3>
                <p className="text-slate-400 text-sm transition-colors duration-300 group-hover:text-slate-300">{value.description}</p>
              </SpotlightCard>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Journey */}
      <section className="py-20 px-6 bg-slate-900/50" data-testid="journey-section">
        <div className="container mx-auto">
          <ScrollReveal animation="fade-up" className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              My Journey
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Perjalanan belajar dan berkembang di dunia engineering
            </p>
          </ScrollReveal>

          <ScrollReveal animation="fade-up" delay={0.2}>
            <div className="max-w-3xl mx-auto">
              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-5 sm:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyan-400 via-blue-500 to-cyan-400"></div>

                {/* Timeline Items */}
                <StaggerGroup staggerDelay={0.15} animation="fade-right" className="space-y-8">
                  {journey.map((item, index) => (
                    <div key={index} className="relative pl-12 sm:pl-20 group" data-testid={`journey-item-${index}`}>
                      <div className="absolute left-2 sm:left-4 w-6 h-6 sm:w-8 sm:h-8 bg-cyan-500 rounded-full border-4 border-slate-900 flex items-center justify-center transition-all duration-300 group-hover:scale-125 group-hover:bg-cyan-400">
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>
                      </div>
                      <Card className="glass-effect border-slate-700 p-4 sm:p-6 transition-all duration-300 group-hover:scale-105 group-hover:border-cyan-500/50 group-hover:shadow-xl group-hover:shadow-cyan-500/20">
                        <div className="text-cyan-400 font-bold text-sm mb-1 transition-colors duration-300 group-hover:text-cyan-300 break-words" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                          {item.year}
                        </div>
                        <h3 className="text-base sm:text-lg font-semibold text-white mb-2 transition-colors duration-300 group-hover:text-cyan-400 break-words" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                          {item.title}
                        </h3>
                        <p className="text-slate-400 text-xs sm:text-sm transition-colors duration-300 group-hover:text-slate-300 break-words">{item.description}</p>
                      </Card>
                    </div>
                  ))}
                </StaggerGroup>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 px-6">
        <div className="container mx-auto">
          <Card className="glass-effect border-slate-700 p-6 sm:p-12 max-w-4xl mx-auto">
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6 break-words" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Engineering Philosophy
              </h2>
              <div className="space-y-4 text-slate-300">
                <p className="text-base sm:text-lg break-words">
                  "Technology should empower, not intimidate. Every complex system started as a simple idea."
                </p>
                <p className="text-sm sm:text-base break-words">
                  Saya percaya bahwa engineer terbaik adalah yang bisa menyelesaikan masalah nyata dengan cara yang elegant dan praktis. 
                  Tidak harus complicated untuk bisa impactful.
                </p>
                <p className="text-sm sm:text-base break-words">
                  Melalui Arnata, saya ingin menjembatani gap antara teori akademis dengan kebutuhan praktis industri. 
                  Membantu orang lain sukses dalam project teknologi mereka adalah kepuasan tersendiri.
                </p>
              </div>
              <div className="mt-8 pt-8 border-t border-slate-700">
                <p className="text-slate-400 text-xs sm:text-sm italic break-words">
                  "The best way to predict the future is to invent it." - Alan Kay
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-slate-900/50">
        <div className="container mx-auto">
          <Card className="glass-effect border-slate-700 p-6 sm:p-12 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 break-words" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Let's Build Something Great
            </h2>
            <p className="text-sm sm:text-base text-slate-300 max-w-2xl mx-auto mb-8 break-words px-2">
              Punya ide project atau butuh bantuan teknis? Mari diskusi bagaimana kita bisa collaborate.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch">
              <Link to="/contact" className="w-full sm:w-auto">
                <StarBorderButton className="btn-primary text-base px-8 py-6 w-full" data-testid="about-contact-cta">
                  Contact Me
                </StarBorderButton>
              </Link>
              <Link to="/portfolio" className="w-full sm:w-auto">
                <Button variant="outline" className="btn-secondary text-base px-8 py-6 w-full" data-testid="about-portfolio-cta">
                  View Portfolio
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}